import {ArrowInSquareIcon, MicromorgiIcon, WalletIcon} from '@/assets/icons/icons'
import {useMe} from '@/features/authentication/queries/useMe'
import {GuestTypeV3} from '@/features/authentication/types'
import {ChannelID} from '@/features/chat/types'
import {useGetMembershipPlans} from '@/features/configure-membership-plans/edit-configure-membership-plans-modal/queries/useGetMembershipPlans'
import {GIFTS_AMOUNTS_LIST} from '@/featuresConfig'
import useMixPanel from '@/integrations/mixpanel/hooks/useMixpanel'
import {MixpanelEventNameE} from '@/integrations/mixpanel/types'
import {useRootStore} from '@/store'
import {
    selectBuyMorgisModal,
    selectConfirmSendGiftModal,
    selectCustomSendGiftModal,
    selectRecurringGiftModal,
    selectWalletModal
} from '@/store/selectors'
import {UserRoleE} from '@/types'
import present from '@assets/images/present.gif'
import {FC, RefObject, useRef} from 'react'
import {createPortal} from 'react-dom'
import {useTranslation} from 'react-i18next'
import {useParams} from 'react-router-dom'
import {MoveMorgiIconAnimation} from '../../animations/move-morgi-icon-animation/MoveMorgiIconAnimation'
import {TypeOfConfirmModalE} from '../../confirm-send-gift-modal/store/createConfirmSendGiftModalSlice'
import {
    StyledAmount,
    StyledGift,
    StyledGiftContent,
    StyledGiftListWrapper,
    StyledMonthlySupportContent,
    StyledPresentImage,
    StyledWallet
} from './style'
import {useGetChannelDetailsV3} from '@/features/chat/queries/useGetChannelDetailsV3'
import {SubscriptionStatusesE} from '@/features/chat/chat.model'
import {isNumber} from 'lodash'

type GiftListProps = {
    hostID: string
    goalId?: string
    iconSize?: {width: number; height: number}
    withWallet?: boolean
    withMonthlySupport?: boolean
    maxDonation?: number
    minDonation?: number
    typeOfConfirmSendModal?: TypeOfConfirmModalE
}
const transitionDuration: number = 6

export const GiftList: FC<GiftListProps> = ({
    hostID,
    goalId,
    withWallet = false,
    withMonthlySupport = false,
    iconSize = {width: 29, height: 29},
    maxDonation,
    minDonation,
    typeOfConfirmSendModal = TypeOfConfirmModalE.giftList
}) => {
    const mixpanel = useMixPanel()
    const gemRef = useRef() as RefObject<HTMLSpanElement> | null
    const {openModal: openConfirmGiftModal, amount: amountStore, typeOfModal} = useRootStore(selectConfirmSendGiftModal)
    const {openModal: openCustomGiftModal} = useRootStore(selectCustomSendGiftModal)
    const {data} = useMe()
    const user = data as GuestTypeV3
    const {openModal: openBuyMorgisModal} = useRootStore(selectBuyMorgisModal)

    const triggerMixpanelEvent = () => {
        const eventData = {
            eventType: MixpanelEventNameE.GiftMicromorgisClick,
            userRole: UserRoleE.Guest,
            userId: hostID
        }
        mixpanel?.triggerBackendEvent({...eventData})
    }

    return (
        <StyledGiftListWrapper>
            {withMonthlySupport && <MonthlySupportButton hostID={hostID} />}
            {withWallet && <WalletButton />}
            {GIFTS_AMOUNTS_LIST.map(amount => {
                const disabledByMaxDonation = maxDonation ? maxDonation < amount : false
                const disabledByMinDonation = minDonation ? minDonation > amount : false
                const disabled = disabledByMaxDonation || disabledByMinDonation || maxDonation === 0
                const isSelected = amountStore === amount
                return (
                    <StyledGift
                        key={amount}
                        aria-disabled={disabled}
                        onClick={() => {
                            if (user?.typeAttributes?.microMorgiBalance >= amount) {
                                if (!disabled) {
                                    openConfirmGiftModal({
                                        amount,
                                        hostID,
                                        goalId,
                                        typeOfModal: typeOfConfirmSendModal
                                    })
                                    triggerMixpanelEvent()
                                }
                            } else {
                                openBuyMorgisModal({})
                            }
                        }}
                    >
                        <StyledGiftContent>
                            <span ref={isSelected ? gemRef : null}>
                                <MicromorgiIcon height={iconSize.height} width={iconSize.width} />
                            </span>
                            <StyledAmount>{amount}</StyledAmount>

                            {isSelected &&
                                typeOfModal === TypeOfConfirmModalE.giftList &&
                                createPortal(
                                    <MoveMorgiIconAnimation
                                        transitionDuration={transitionDuration}
                                        iconSize={iconSize}
                                        gemRef={gemRef}
                                    />,
                                    document.body,
                                    `animation-${amount}`
                                )}
                        </StyledGiftContent>
                    </StyledGift>
                )
            })}
            <StyledGift
                aria-disabled={maxDonation === undefined ? false : isNumber(maxDonation) && maxDonation === 0}
                onClick={() => {
                    if (!(maxDonation === undefined ? false : isNumber(maxDonation) && maxDonation === 0))
                        openCustomGiftModal({
                            hostID,
                            maxDonation,
                            goalId,
                            minDonation
                        })
                }}
            >
                <StyledGiftContent>
                    <ArrowInSquareIcon />
                </StyledGiftContent>
            </StyledGift>
        </StyledGiftListWrapper>
    )
}

const WalletButton: FC = () => {
    const {data: user} = useMe()
    const {openModal: openWalletModal} = useRootStore(selectWalletModal)
    return (
        <StyledGift
            onClick={() => {
                openWalletModal({})
            }}
        >
            <StyledGiftContent>
                <WalletIcon />
                <StyledWallet>
                    {
                        (
                            user?.typeAttributes as {
                                microMorgiBalance: number
                            }
                        )?.microMorgiBalance
                    }
                </StyledWallet>
            </StyledGiftContent>
        </StyledGift>
    )
}

type MonthlySupportButtonProps = {hostID: string}
const MonthlySupportButton: FC<MonthlySupportButtonProps> = ({hostID}) => {
    const {channel} = useParams()
    const {data: channelDetails, isLoading} = useGetChannelDetailsV3(channel as ChannelID)
    const {t} = useTranslation()
    const {openModal: openRecurringGiftModal} = useRootStore(selectRecurringGiftModal)
    const {data: packagesList} = useGetMembershipPlans(
        !isLoading && channelDetails?.metadata?.subscription?.amount ? hostID : undefined
    )
    const selectedAmount =
        channelDetails?.metadata?.subscription?.status === SubscriptionStatusesE.ACTIVE
            ? packagesList?.find(pack => pack.amount === channelDetails?.metadata?.subscription?.amount)
            : undefined
    return (
        <StyledGift
            onClick={() => {
                openRecurringGiftModal({hostID, selectedAmount})
            }}
        >
            <StyledGiftContent>
                <StyledMonthlySupportContent>
                    {selectedAmount ? t('common:upgrade') : t('common:start')}
                </StyledMonthlySupportContent>
                <StyledPresentImage src={present} />
                <StyledMonthlySupportContent>{t('chat:teaser_tooltip_monthly_support')}</StyledMonthlySupportContent>
            </StyledGiftContent>
        </StyledGift>
    )
}
